import { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Pill, ChevronRight } from 'lucide-react';
import api from '@/lib/api';

export default function FarmacoAutocomplete({ value, onChange, onSelectDosaggio, onSelectFull, placeholder, ...props }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => { setQuery(value || ''); }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) { setOpen(false); setExpanded(null); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchFarmaci = async (q) => {
    if (!q || q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const res = await api.get('/utility/farmaci', { params: { q, limit: 15 } });
      setResults(res.data);
      setOpen(res.data.length > 0);
      setExpanded(null);
    } catch {
      setResults([]);
    }
    setLoading(false);
  };

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchFarmaci(val), 300);
  };

  const handleSelectDrug = (item, idx) => {
    if (item.dosaggi && item.dosaggi.length > 0) {
      setExpanded(expanded === idx ? null : idx);
    } else {
      setQuery(item.nome);
      onChange(item.nome);
      setOpen(false);
      setExpanded(null);
    }
  };

  const handleSelectDosaggio = (item, dosaggio) => {
    const label = `${item.nome} ${dosaggio}`;
    setQuery(label);
    onChange(label);
    if (onSelectDosaggio) onSelectDosaggio(dosaggio);
    if (onSelectFull) onSelectFull(item, dosaggio);
    setOpen(false);
    setExpanded(null);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={query}
          onChange={handleChange}
          onFocus={() => { if (results.length > 0) setOpen(true); }}
          placeholder={placeholder || "Cerca farmaco AIFA..."}
          className="pr-8"
          data-testid="farmaco-input"
          {...props}
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <div className="w-4 h-4 border-2 border-blue-300 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
      </div>
      {open && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[320px] overflow-y-auto" data-testid="farmaco-dropdown">
          {results.map((item, i) => (
            <div key={i}>
              <button
                className={`w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 text-sm transition-colors border-b border-slate-50 last:border-0 ${expanded === i ? 'bg-blue-50' : ''}`}
                onClick={() => handleSelectDrug(item, i)}
                data-testid={`farmaco-option-${i}`}
              >
                <Pill className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="block truncate font-medium">{item.nome}</span>
                  {item.principio_attivo && (
                    <span className="block text-[10px] text-slate-400 truncate">{item.principio_attivo}</span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {item.dosaggi && item.dosaggi.length > 0 && (
                    <Badge variant="outline" className="text-[9px] text-emerald-600 border-emerald-200">
                      {item.dosaggi.length} dosaggi
                    </Badge>
                  )}
                  {item.forme && item.forme.length > 0 && (
                    <Badge variant="outline" className="text-[9px] text-slate-500 border-slate-200 max-w-[100px] truncate">
                      {item.forme[0]}
                    </Badge>
                  )}
                  {item.dosaggi && item.dosaggi.length > 0 && (
                    <ChevronRight className={`w-3 h-3 text-slate-400 transition-transform ${expanded === i ? 'rotate-90' : ''}`} />
                  )}
                </div>
              </button>
              {expanded === i && item.dosaggi && item.dosaggi.length > 0 && (
                <div className="bg-slate-50 border-b border-slate-100">
                  <div className="px-3 py-1">
                    <p className="text-[10px] text-slate-500 font-medium mb-1">Seleziona dosaggio:</p>
                    <div className="flex flex-wrap gap-1 pb-1">
                      {item.dosaggi.map((dos, j) => (
                        <button
                          key={j}
                          className="px-2 py-0.5 text-xs bg-white border border-blue-200 text-blue-700 rounded-md hover:bg-blue-100 hover:border-blue-300 transition-colors"
                          onClick={() => handleSelectDosaggio(item, dos)}
                          data-testid={`dosaggio-option-${i}-${j}`}
                        >
                          {dos}
                        </button>
                      ))}
                    </div>
                    {item.forme && item.forme.length > 1 && (
                      <p className="text-[9px] text-slate-400 mt-0.5">
                        Forme: {item.forme.join(', ')}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
          <div className="px-3 py-1.5 bg-slate-50 text-[9px] text-slate-400 text-center">
            Fonte: Banca Dati AIFA - Farmaci Autorizzati in Italia
          </div>
        </div>
      )}
    </div>
  );
}
